import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import Markdown from "markdown-to-jsx";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  LinearProgress,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const HEADING_PREFIX = /^#+\s*/;
const MAX_RECORDING_MS = 120000;

const audioExtension = type => {
  if (type.includes("ogg")) return "ogg";
  if (type.includes("mp4")) return "mp4";
  if (type.includes("mpeg")) return "mp3";
  return "webm";
};

const useStyles = makeStyles()(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: theme.palette.background.default
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    flexShrink: 0
  },
  topTitle: {
    fontWeight: 600,
    whiteSpace: "nowrap"
  },
  progress: {
    height: 2,
    flexShrink: 0
  },
  start: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 2)
  },
  startColumn: {
    width: "100%",
    maxWidth: 720,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  startTitle: {
    fontSize: 26,
    fontWeight: 700,
    textAlign: "center"
  },
  hint: {
    fontSize: 14,
    color: theme.palette.text.secondary
  },
  centered: {
    textAlign: "center"
  },
  inputBox: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    background: theme.palette.background.paper
  },
  input: {
    flex: 1,
    fontSize: 15,
    padding: theme.spacing(1, 0)
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  chipsCentered: {
    justifyContent: "center"
  },
  sourceChip: {
    alignSelf: "flex-start",
    maxWidth: "100%"
  },
  writeMyself: {
    alignSelf: "center",
    textTransform: "none"
  },
  workspace: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 480px",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
      gridTemplateRows: "minmax(0, 1fr) minmax(0, 1fr)"
    }
  },
  chat: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(3),
    ...theme.scrollbarStyles
  },
  messagesInner: {
    width: "100%",
    maxWidth: 760,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5)
  },
  bubble: {
    maxWidth: "85%",
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  assistantBubble: {
    alignSelf: "flex-start",
    background: theme.palette.action.hover
  },
  userBubble: {
    alignSelf: "flex-end",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  composer: {
    padding: theme.spacing(1.5, 3, 2),
    flexShrink: 0
  },
  composerInner: {
    maxWidth: 760,
    margin: "0 auto"
  },
  draft: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: theme.palette.background.paper
  },
  draftHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
    fontSize: 15
  },
  draftBody: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles
  },
  draftHeading: {
    fontSize: 15,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.75),
    "&:first-of-type": { marginTop: 0 }
  },
  draftItem: {
    fontSize: 14,
    lineHeight: 1.5,
    paddingLeft: theme.spacing(2),
    position: "relative",
    marginBottom: theme.spacing(0.75),
    "&::before": {
      content: '"•"',
      position: "absolute",
      left: 2,
      color: theme.palette.text.secondary
    }
  },
  draftMarkdown: {
    fontSize: 14,
    lineHeight: 1.55,
    wordBreak: "break-word",
    "& h1, & h2, & h3, & h4": {
      fontSize: 15,
      fontWeight: 700,
      margin: theme.spacing(2, 0, 0.75)
    },
    "& > :first-child": { marginTop: 0 },
    "& p": { margin: theme.spacing(0, 0, 1) },
    "& ul, & ol": { margin: theme.spacing(0, 0, 1), paddingLeft: theme.spacing(2.5) },
    "& li": { marginBottom: theme.spacing(0.5) }
  },
  draftEditor: {
    height: "100%",
    "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" },
    "& textarea": { fontSize: 14, lineHeight: 1.6 }
  },
  editHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 2, 1.5)
  }
}));

const DraftView = ({ text, emptyText, classes }) => {
  const lines = String(text || "")
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.length) return <Typography className={classes.hint}>{emptyText}</Typography>;
  return lines.map((line, index) =>
    HEADING_PREFIX.test(line) ? (
      <div key={index} className={classes.draftHeading}>
        {line.replace(HEADING_PREFIX, "")}
      </div>
    ) : (
      <div key={index} className={classes.draftItem}>
        {line.replace(/^[-*]\s+/, "")}
      </div>
    )
  );
};

const MarkdownDraftView = ({ text, emptyText, classes }) =>
  String(text || "").trim() ? (
    <div className={classes.draftMarkdown}>
      <Markdown options={{ forceBlock: true }}>{text}</Markdown>
    </div>
  ) : (
    <Typography className={classes.hint}>{emptyText}</Typography>
  );

const AiDraftAssistant = ({
  open,
  onClose,
  title,
  topBarContent,
  initialText = "",
  draftFormat = "lines",
  externalBusy = false,
  labels,
  suggestions = [],
  onAsk,
  onUploadSource,
  onStart
}) => {
  const { classes, cx } = useStyles();
  const [text, setText] = useState(initialText);
  const [goal, setGoal] = useState("");
  const [turns, setTurns] = useState([]);
  const [answer, setAnswer] = useState("");
  const [thinking, setThinking] = useState(false);
  const [source, setSource] = useState(null);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [editingDraft, setEditingDraft] = useState(false);
  const [writingMyself, setWritingMyself] = useState(false);
  const messagesRef = useRef(null);
  const sourceInputRef = useRef(null);
  const recorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const transcribe = async blob => {
    setTranscribing(true);
    try {
      const body = new FormData();
      body.append("audio", blob, `dictado.${audioExtension(blob.type)}`);
      body.append("language", i18n.language || "es");
      const { data } = await api.post("/ai-assistant/transcribe", body);
      if (data.text) setAnswer(prev => (prev.trim() ? `${prev.trim()} ${data.text}` : data.text));
    } catch (err) {
      toastError(err);
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      toastError(new Error(i18n.t("aiAssistant.micDenied")));
      return;
    }
    const recorder = new MediaRecorder(stream);
    const chunks = [];
    const limitTimer = setTimeout(stopRecording, MAX_RECORDING_MS);
    recorder.ondataavailable = event => {
      if (event.data.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      clearTimeout(limitTimer);
      stream.getTracks().forEach(track => track.stop());
      recorderRef.current = null;
      setRecording(false);
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      if (blob.size && !recorder.discarded) transcribe(blob);
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  };

  useEffect(() => {
    if (open) return undefined;
    if (recorderRef.current) {
      recorderRef.current.discarded = true;
      stopRecording();
    }
    return undefined;
  }, [open]);

  useEffect(
    () => () => {
      if (recorderRef.current) {
        recorderRef.current.discarded = true;
        stopRecording();
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setGoal("");
    setTurns([]);
    setAnswer("");
    setSource(null);
    setEditingDraft(false);
    setWritingMyself(false);
  }, [open, initialText]);

  useEffect(() => {
    const node = messagesRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [turns, thinking]);

  const started = turns.length > 0;
  const showWorkspace = started || writingMyself || Boolean(initialText);
  const busy = externalBusy || thinking || uploadingSource || recording || transcribing;
  const canStart = Boolean(answer.trim() || source);

  const ask = async (topic, nextTurns) => {
    setThinking(true);
    try {
      const data = await onAsk({ goal: topic, turns: nextTurns, draft: text, sourceId: source?.sourceId });
      setTurns([...nextTurns, { role: "assistant", content: data.message }]);
      if (data.draft) setText(data.draft);
    } catch (err) {
      toastError(err);
      setTurns(nextTurns);
    } finally {
      setThinking(false);
    }
  };

  const uploadSource = async file => {
    if (!file) return;
    setUploadingSource(true);
    try {
      setSource(await onUploadSource(file));
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingSource(false);
      if (sourceInputRef.current) sourceInputRef.current.value = "";
    }
  };

  const start = topic => {
    const typed = String(topic || "").trim();
    const value = typed || (source && labels.sourceGoal ? labels.sourceGoal(source.name) : "");
    if (!value || busy) return;
    setGoal(value);
    if (onStart) onStart({ typed, source });
    const nextTurns = [{ role: "user", content: value }];
    setTurns(nextTurns);
    setAnswer("");
    setEditingDraft(false);
    ask(value, nextTurns);
  };

  const send = () => {
    const value = answer.trim();
    if (!value || busy) return;
    const nextTurns = [...turns, { role: "user", content: value }];
    setTurns(nextTurns);
    setAnswer("");
    ask(goal, nextTurns);
  };

  const submit = () => (started ? send() : start(answer));

  const restart = () => {
    setGoal("");
    setTurns([]);
    setAnswer("");
  };

  const writeMyself = () => {
    setWritingMyself(true);
    setEditingDraft(true);
  };

  const handleKeyDown = event => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  const sourceChip = source && (
    <Chip
      className={classes.sourceChip}
      size="small"
      color="primary"
      icon={<AttachFileOutlinedIcon />}
      label={source.truncated && labels.sourceTruncated ? labels.sourceTruncated(source.name) : source.name}
      onDelete={started ? undefined : () => setSource(null)}
    />
  );

  const inputBox = (
    <div className={classes.inputBox}>
      {!started && onUploadSource && (
        <>
          <input
            ref={sourceInputRef}
            type="file"
            accept="application/pdf,.pdf,text/plain,.txt"
            hidden
            onChange={e => uploadSource(e.target.files?.[0])}
          />
          <Tooltip title={labels.attachSource}>
            <span>
              <IconButton onClick={() => sourceInputRef.current?.click()} disabled={busy}>
                <AttachFileOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      <InputBase
        className={classes.input}
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          recording
            ? i18n.t("aiAssistant.listening")
            : transcribing
              ? i18n.t("aiAssistant.transcribing")
              : started
                ? labels.answerPlaceholder
                : labels.goalPlaceholder
        }
        multiline
        maxRows={6}
        autoFocus
        disabled={thinking || uploadingSource || recording || transcribing}
      />
      <Tooltip title={i18n.t(recording ? "aiAssistant.stopDictation" : "aiAssistant.dictate")}>
        <span>
          {transcribing ? (
            <IconButton disabled>
              <CircularProgress size={20} />
            </IconButton>
          ) : (
            <IconButton
              color={recording ? "error" : "default"}
              onClick={recording ? stopRecording : startRecording}
              disabled={externalBusy || thinking || uploadingSource}
            >
              {recording ? <StopCircleOutlinedIcon /> : <MicNoneOutlinedIcon />}
            </IconButton>
          )}
        </span>
      </Tooltip>
      <Tooltip title={started ? labels.send : labels.start}>
        <span>
          <IconButton color="primary" onClick={submit} disabled={busy || (started ? !answer.trim() : !canStart)}>
            <SendOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );

  const suggestionChips = centered =>
    suggestions.length > 0 && (
      <div className={cx(classes.chips, centered && classes.chipsCentered)}>
        {suggestions.map(suggestion => (
          <Chip key={suggestion} variant="outlined" label={suggestion} onClick={() => start(suggestion)} disabled={busy} />
        ))}
      </div>
    );

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullScreen>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <IconButton size="small" onClick={onClose} disabled={busy}>
            <ArrowBackOutlinedIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" className={classes.topTitle}>
            {title}
          </Typography>
          {topBarContent({ text, busy })}
        </div>
        {busy ? <LinearProgress className={classes.progress} /> : <div className={classes.progress} />}

        {!showWorkspace ? (
          <div className={classes.start}>
            <div className={classes.startColumn}>
              <Typography className={classes.startTitle}>{labels.startTitle}</Typography>
              <Typography className={cx(classes.hint, classes.centered)}>{labels.startHint}</Typography>
              {sourceChip}
              {inputBox}
              {suggestionChips(true)}
              <Button className={classes.writeMyself} onClick={writeMyself} disabled={busy}>
                {labels.writeMyself}
              </Button>
            </div>
          </div>
        ) : (
          <div className={classes.workspace}>
            <div className={classes.chat}>
              <div className={classes.messages} ref={messagesRef}>
                <div className={classes.messagesInner}>
                  {!started && (
                    <>
                      <Typography className={classes.hint}>{labels.intro}</Typography>
                      {sourceChip}
                      {suggestionChips(false)}
                    </>
                  )}
                  {turns.map((turn, index) => (
                    <div
                      key={index}
                      className={cx(classes.bubble, turn.role === "assistant" ? classes.assistantBubble : classes.userBubble)}
                    >
                      {turn.content}
                    </div>
                  ))}
                  {thinking && <div className={cx(classes.bubble, classes.assistantBubble)}>{labels.thinking}</div>}
                </div>
              </div>
              <div className={classes.composer}>
                <div className={classes.composerInner}>{inputBox}</div>
              </div>
            </div>

            <div className={classes.draft}>
              <div className={classes.draftHeader}>
                <AutoAwesomeOutlinedIcon fontSize="small" color="primary" />
                <span style={{ flex: 1 }}>{labels.draftTitle}</span>
                {started && (
                  <Tooltip title={labels.restart}>
                    <span>
                      <IconButton size="small" onClick={restart} disabled={busy}>
                        <RestartAltOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  startIcon={editingDraft ? <CheckOutlinedIcon /> : <EditOutlinedIcon />}
                  onClick={() => setEditingDraft(prev => !prev)}
                  disabled={thinking}
                >
                  {editingDraft ? labels.doneEditing : labels.editDraft}
                </Button>
              </div>
              <div className={classes.draftBody}>
                {editingDraft ? (
                  <TextField
                    className={classes.draftEditor}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    multiline
                    fullWidth
                    autoFocus
                  />
                ) : (
                  (draftFormat === "markdown" ? MarkdownDraftView : DraftView)({ text, emptyText: labels.draftEmpty, classes })
                )}
              </div>
              {editingDraft && <Typography className={classes.editHint}>{labels.editHint}</Typography>}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export const assistantLabels = (prefix, extra = {}) => ({
  startTitle: i18n.t(`${prefix}.startTitle`),
  startHint: i18n.t(`${prefix}.startHint`),
  writeMyself: i18n.t(`${prefix}.writeMyself`),
  intro: i18n.t(`${prefix}.assistantIntro`),
  goalPlaceholder: i18n.t(`${prefix}.goalPlaceholder`),
  answerPlaceholder: i18n.t(`${prefix}.answerPlaceholder`),
  draftTitle: i18n.t(`${prefix}.draftTitle`),
  draftEmpty: i18n.t(`${prefix}.draftEmpty`),
  editHint: i18n.t(`${prefix}.editHint`),
  editDraft: i18n.t("aiAssistant.editDraft"),
  doneEditing: i18n.t("aiAssistant.doneEditing"),
  restart: i18n.t("aiAssistant.restart"),
  thinking: i18n.t("aiAssistant.thinking"),
  start: i18n.t("aiAssistant.start"),
  send: i18n.t("aiAssistant.send"),
  ...extra
});

export default AiDraftAssistant;
